import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTutorial } from '../../contexts/TutorialContext';
import { buildTutorialTargetMissingHint } from '../../tutorials/buildTargetMissingHint';
import { renderTutorialSectionLabel } from '../../tutorials/formatTutorialSectionLabel';
import {
  closeTutorialOverlayElements,
  dismissTutorialUnsavedConfirmOverlays,
  expandTutorialTargetsWithStackedOverlays,
  getTutorialStackedOverlaysAbove,
  isTutorialClickOnTarget,
  measureTutorialSpotlightHoles,
  unionAllTutorialSpotlightHoles,
  resolveTutorialTargets,
  splitTutorialSelectors,
  tutorialSelectorExistsInDom,
  TUTORIAL_LEAVE_CONFIRM_SELECTOR,
  TUTORIAL_NESTED_OVERLAY_BACKDROP_SELECTOR,
  TUTORIAL_OVERLAY_CLOSE_BUTTON_SELECTOR,
  TUTORIAL_STACKED_OVERLAY_SELECTOR,
  unionTutorialTargetRect,
  type TutorialSpotlightHole,
} from '../../tutorials/resolveTutorialTarget';
import {
  PAGE_ZOOM_CHANGED_EVENT,
  clientPointToLayoutPoint,
  clientRectToLayoutRect,
  getLayoutViewportSize,
  readPageZoom,
} from '../../utils/pageZoom';
import {
  TUTORIAL_DESKTOP_ONLY_MESSAGE,
  isTutorialDesktopViewport,
} from '../../utils/tutorialViewport';
import './tutorial.css';
import CachedAppImage from '../common/CachedAppImage';

function TutorialMascotAvatar() {
  return <CachedAppImage file="character_0.webp" className="cp-tutorial-mascot-avatar" />;
}
const HOLE_PADDING = 6;
/** spotlightSeparate: 좁은 간격 항목이 서로 붙지 않도록 패딩을 줄인다 */
const HOLE_PADDING_SEPARATE = 2;
const POLL_INTERVAL_MS = 180;
/** 이 시간 동안 타깃을 못 찾으면 "화면 다시 불러오기"를 보여준다 */
const RECOVER_HINT_MS = 2200;
const BUBBLE_WIDTH = 380;
const BUBBLE_CELEBRATION_WIDTH = 440;
const BUBBLE_SECTION_WIDTH = 400;
const BUBBLE_MARGIN = 16;
/** top/bottom 배치에서 화면 밖으로 넘어가지 않도록 클램프할 때 쓰는 말풍선 예상 최대 높이 */
const BUBBLE_EST_HEIGHT = 260;
/** 시작/완료 카드(중요 안내 포함) 실제 높이가 커질 수 있어 여유 있게 잡는다 */
const BUBBLE_CELEBRATION_EST_HEIGHT = 480;
const BUBBLE_SECTION_EST_HEIGHT = 250;
const BUBBLE_GAP = 18;
/**
 * 말풍선 꼬리(삼각형)를 보여줄 최대 간격.
 * 하이라이트와 이보다 멀면 꼬리가 허공을 가리키므로 숨긴다.
 */
const BUBBLE_TAIL_MAX_EDGE_GAP = 48;
/** 꼬리 끝이 하이라이트 축에서 벗어나면 숨길 여유 */
const BUBBLE_TAIL_MAX_ALIGN_SLACK = 72;

function formatTutorialSectionTitle(section: string): string {
  return section.replace(/-/g, ' · ');
}
/**
 * 모달/팝업을 "다음" 클릭으로 진행하도록 설계된 단계(`manualAdvance`)에서,
 * 모달 자체의 닫기·취소·확인 버튼으로 먼저 닫히면 의도한 흐름(설명 → "다음")이 끊긴다.
 * 이런 버튼은 하이라이트 안에 있어도 클릭을 막아 반드시 "다음"으로만 닫히게 한다.
 * (followUp이 있는 단계뿐 아니라, 이미 열린 모달 자체를 targetSelector로 잡는 단계도 포함)
 */
const TUTORIAL_MANUAL_ADVANCE_CLOSE_BLOCK_SELECTOR = [
  TUTORIAL_OVERLAY_CLOSE_BUTTON_SELECTOR,
  /** 살펴보기 단계에서 반영·저장·확인으로 모달이 닫히지 않게 잠근다 */
  '.cp-modal-footer .cp-btn-primary',
  '.cp-cross-curricular-catalog-picker-apply',
  /** 프로필 이미지 모달 "확인"은 적용 후 닫히므로, 기능만 알아보는 단계에서는 잠근다 */
  '.cp-profile-img-btn.is-confirm',
  '.cp-program-note-popup-actions',
  '.cp-program-unit-popup-actions',
].join(', ');

/**
 * "저장하지 않은 변경사항" 확인 모달은 어느 단계에서든 예고 없이 뜰 수 있다.
 * 이 모달이 열리면 현재 단계와 무관하게 항상 하이라이트·조작 가능하게 하고,
 * 공통 안내 문구로 저장 여부를 선택해 계속 진행하도록 안내한다.
 */
const TUTORIAL_LEAVE_CONFIRM_MASCOT =
  '이동하기 전에 변경된 내용을 저장할지 확인하는 창이에요. 원하는 버튼을 눌러 계속 진행해 주세요.';

type Rect = { top: number; left: number; width: number; height: number };
type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center' | 'below-nav';

const TOPNAV_FALLBACK_HEIGHT = 36;
const EXIT_BTN_GAP_BELOW_NAV = 8;
/** 튜토리얼 종료 버튼 예상 높이 — 실측 전 폴백 */
const EXIT_BTN_EST_HEIGHT = 44;

function measureTopnavBottom(): number {
  if (typeof document === 'undefined') return TOPNAV_FALLBACK_HEIGHT;
  const nav = document.querySelector('.cp-topnav') as HTMLElement | null;
  if (!nav) return TOPNAV_FALLBACK_HEIGHT;
  const bottom = clientRectToLayoutRect(nav.getBoundingClientRect()).bottom;
  return Number.isFinite(bottom) && bottom > 0 ? bottom : TOPNAV_FALLBACK_HEIGHT;
}

/** 종료 버튼 바로 아래(말풍선이 겹치지 않는 최소 top) */
function getBubbleMinTopBelowExit(): number {
  if (typeof document !== 'undefined') {
    const exitBtn = document.querySelector('.cp-tutorial-exit-btn') as HTMLElement | null;
    if (exitBtn) {
      const rect = clientRectToLayoutRect(exitBtn.getBoundingClientRect());
      if (rect.width > 0 && rect.height > 0) {
        return rect.bottom + BUBBLE_GAP;
      }
    }
  }
  return (
    measureTopnavBottom() +
    EXIT_BTN_GAP_BELOW_NAV +
    EXIT_BTN_EST_HEIGHT +
    BUBBLE_GAP
  );
}

/**
 * 전역 튜토리얼(미션) 오버레이. MainLayout에 한 번만 마운트한다.
 * - 타깃이 여러 개면 각각 둥근 구멍으로 밝게 표시 (열 전체를 한 직사각형으로 덮지 않음)
 * - SVG 마스크로 딤을 깔아 구멍 모양이 border-radius를 그대로 따름
 */
function sameTargetEls(a: HTMLElement[], b: HTMLElement[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((el, i) => el === b[i]);
}

const TutorialOverlayHost: React.FC = () => {
  const { activeTutorial, activeStepIndex, goToNextStep, goToStepById, exitTutorial } = useTutorial();
  const location = useLocation();
  const [targetEls, setTargetEls] = React.useState<HTMLElement[]>([]);
  const [holes, setHoles] = React.useState<TutorialSpotlightHole[]>([]);
  const [unionRect, setUnionRect] = React.useState<Rect | null>(null);
  const [searching, setSearching] = React.useState(false);
  const [targetMissing, setTargetMissing] = React.useState(false);
  /** 사용자가 말풍선을 드래그해 임시로 옮긴 위치. 단계가 바뀌면 초기화한다. */
  const [bubbleDragPos, setBubbleDragPos] = React.useState<{ left: number; top: number } | null>(null);
  const [bubbleDragging, setBubbleDragging] = React.useState(false);
  /** 등장 애니메이션 1회 재생 후 true — 클래스 토글 시 재재생(깜박임) 방지 */
  const [bubbleAnimationDone, setBubbleAnimationDone] = React.useState(false);
  const bubbleDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    /** 임계 거리 이상 움직인 뒤에만 true (단순 클릭과 구분) */
    active: boolean;
  } | null>(null);
  const bubbleElRef = React.useRef<HTMLDivElement | null>(null);
  const exitBtnRef = React.useRef<HTMLButtonElement | null>(null);
  /**
   * 종료 버튼과 겹칠 때 말풍선을 아래로 내린 보정 위치.
   * (center의 translateY 등 예상 높이만으로는 부족한 경우를 layout으로 보정)
   * centerX: true면 좌우 중앙(left:50% + translateX(-50%))을 유지하고 top만 보정한다.
   */
  const [bubbleExitSafePos, setBubbleExitSafePos] = React.useState<{
    left: number;
    top: number;
    centerX?: boolean;
  } | null>(null);
  /** 종료 버튼 하단 + 간격 — 말풍선 top 하한 */
  const [exitClearTop, setExitClearTop] = React.useState(() => getBubbleMinTopBelowExit());
  const [followUpEls, setFollowUpEls] = React.useState<HTMLElement[]>([]);
  const [followUpHoles, setFollowUpHoles] = React.useState<TutorialSpotlightHole[]>([]);
  const [followUpUnionRect, setFollowUpUnionRect] = React.useState<Rect | null>(null);
  const followUpWasActiveRef = React.useRef(false);
  const [leaveConfirmEls, setLeaveConfirmEls] = React.useState<HTMLElement[]>([]);
  const [leaveConfirmHoles, setLeaveConfirmHoles] = React.useState<TutorialSpotlightHole[]>([]);
  const [leaveConfirmUnionRect, setLeaveConfirmUnionRect] = React.useState<Rect | null>(null);
  const leaveConfirmWasActiveRef = React.useRef(false);
  const [manualAdvanceOverride, setManualAdvanceOverride] = React.useState<boolean | null>(null);
  /** `manualAdvanceEnableWhen` 셀렉터 충족 여부. null이면 게이트 없음. */
  const [manualAdvanceEnableReady, setManualAdvanceEnableReady] = React.useState<boolean | null>(null);
  /** 구간(section) 소타이틀 카드를 이미 닫은 stepKey. 같으면 실제 단계 안내로 진행. */
  const [sectionCardDismissedKey, setSectionCardDismissedKey] = React.useState<string | null>(null);
  const scrolledRef = React.useRef(false);
  const missingSinceRef = React.useRef<number | null>(null);
  const stepStartPathRef = React.useRef('');
  /** 이미 진행을 시도한 stepKey. 이전 단계의 delayed advance가 다음 단계를 오염시키지 않게 한다. */
  const advancedForStepKeyRef = React.useRef<string | null>(null);
  /** blockTargetClick 오버레이 위에서 터치 스크롤을 아래로 전달할 때 사용 */
  const clickBlockTouchRef = React.useRef<{
    x: number;
    y: number;
    scrollEl: HTMLElement | null;
  } | null>(null);

  const rawStep = activeTutorial ? activeTutorial.steps[activeStepIndex] : null;
  /** `conditionalOverride.whenSelectorExists` 충족 여부 (단계 진입 후 폴링) */
  const [conditionalOverrideOn, setConditionalOverrideOn] = React.useState(false);
  const conditionalSel = rawStep?.conditionalOverride?.whenSelectorExists ?? null;
  React.useEffect(() => {
    if (!conditionalSel) {
      setConditionalOverrideOn(false);
      return undefined;
    }
    const check = () => {
      let found = false;
      try {
        found = document.querySelector(conditionalSel) != null;
      } catch {
        found = false;
      }
      setConditionalOverrideOn((prev) => (prev === found ? prev : found));
    };
    check();
    const id = window.setInterval(check, 300);
    return () => window.clearInterval(id);
  }, [conditionalSel, activeStepIndex]);
  const step = React.useMemo(
    () =>
      rawStep && rawStep.conditionalOverride && conditionalOverrideOn
        ? { ...rawStep, ...rawStep.conditionalOverride.override }
        : rawStep,
    [rawStep, conditionalOverrideOn]
  );
  const totalSteps = activeTutorial?.steps.length ?? 0;
  const stepKey = activeTutorial ? `${activeTutorial.id}:${step?.id ?? activeStepIndex}` : null;
  const stepKeyRef = React.useRef(stepKey);
  stepKeyRef.current = stepKey;
  const advanceGoToStepIdRef = React.useRef(step?.advanceGoToStepId);
  advanceGoToStepIdRef.current = step?.advanceGoToStepId;

  /** 화면 배율 변경 시 하이라이트·말풍선 좌표를 다시 잰다 */
  const [pageZoom, setPageZoom] = React.useState(() => readPageZoom());
  React.useEffect(() => {
    const sync = (e?: Event) => {
      const detail = (e as CustomEvent<{ zoom?: number }> | undefined)?.detail;
      setPageZoom(
        typeof detail?.zoom === 'number' && Number.isFinite(detail.zoom)
          ? detail.zoom
          : readPageZoom()
      );
    };
    sync();
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, sync as EventListener);
    return () => window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, sync as EventListener);
  }, []);

  /** PC(가로 900+)가 아니면 진행 중인 튜토리얼을 종료한다 */
  React.useEffect(() => {
    if (!activeTutorial) return undefined;
    let exited = false;
    const enforceDesktop = () => {
      if (exited || isTutorialDesktopViewport()) return;
      exited = true;
      exitTutorial();
      window.alert(TUTORIAL_DESKTOP_ONLY_MESSAGE);
    };
    enforceDesktop();
    window.addEventListener('resize', enforceDesktop);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, enforceDesktop as EventListener);
    return () => {
      window.removeEventListener('resize', enforceDesktop);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, enforceDesktop as EventListener);
    };
  }, [activeTutorial, exitTutorial]);

  /**
   * @param forStepKey 예약 시점의 stepKey. 단계가 바뀐 뒤 도착한 delayed 콜백은 무시한다.
   */
  const tryAdvanceStep = React.useCallback(
    (forStepKey?: string | null) => {
      const currentKey = stepKeyRef.current;
      if (!currentKey) return;
      if (forStepKey != null && forStepKey !== currentKey) return;
      if (advancedForStepKeyRef.current === currentKey) return;
      advancedForStepKeyRef.current = currentKey;
      const goToId = advanceGoToStepIdRef.current;
      if (goToId) {
        goToStepById(goToId);
        return;
      }
      goToNextStep();
    },
    [goToNextStep, goToStepById]
  );
  const tryAdvanceStepRef = React.useRef(tryAdvanceStep);
  tryAdvanceStepRef.current = tryAdvanceStep;
  const scheduleTryAdvanceStep = React.useCallback((delayMs = 0) => {
    const keyAtSchedule = stepKeyRef.current;
    window.setTimeout(() => {
      tryAdvanceStepRef.current(keyAtSchedule);
    }, delayMs);
  }, []);
  const scheduleTryAdvanceStepRef = React.useRef(scheduleTryAdvanceStep);
  scheduleTryAdvanceStepRef.current = scheduleTryAdvanceStep;

  /**
   * advanceSelector 클릭 후 "타깃이 사라질 때까지 대기" 상태.
   * 모달이 닫히면 targetEls가 비워지며 클릭 effect가 cleanup되는데,
   * 그때 대기를 취소해 버리면 저장 후에도 단계가 넘어가지 않는다.
   * 그래서 이 대기는 stepKey가 바뀔 때만 취소한다.
   */
  const advanceWaitRef = React.useRef<{ cancelled: boolean; timer: number | null } | null>(null);
  /**
   * advanceWhenSelectorGone용: 단계 내에서 한 번이라도 대상이 보였는지.
   * tryAdvanceStep 등 deps 변동으로 effect가 재실행돼도, 팝업이 이미 닫힌 뒤
   * seen=false로 초기화되면 영구 정지되므로 stepKey 단위로만 리셋한다.
   */
  const advanceGoneSeenRef = React.useRef(false);

  React.useEffect(() => {
    // stepKey가 바뀌면 새 단계에서 다시 진행할 수 있다(키 단위 가드).
    advanceGoneSeenRef.current = false;
    stepStartPathRef.current = location.pathname;
    setBubbleDragPos(null);
    setBubbleDragging(false);
    setBubbleAnimationDone(false);
    setBubbleExitSafePos(null);
    bubbleDragRef.current = null;
    if (advanceWaitRef.current) {
      advanceWaitRef.current.cancelled = true;
      if (advanceWaitRef.current.timer != null) window.clearTimeout(advanceWaitRef.current.timer);
      advanceWaitRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  /** followUp·구간 카드 전환 때도 종료 버튼 회피 보정을 다시 잡는다. */
  React.useEffect(() => {
    setBubbleExitSafePos(null);
  }, [followUpEls.length, leaveConfirmEls.length, sectionCardDismissedKey]);

  /** 종료 버튼 위치를 추적해 말풍선이 겹치지 않도록 한다. */
  React.useLayoutEffect(() => {
    if (!activeTutorial) return undefined;
    let alive = true;
    const update = () => {
      if (!alive) return;
      const exitEl = exitBtnRef.current;
      const next = exitEl
        ? (() => {
            const r = clientRectToLayoutRect(exitEl.getBoundingClientRect());
            return r.height > 0 ? r.bottom + BUBBLE_GAP : getBubbleMinTopBelowExit();
          })()
        : getBubbleMinTopBelowExit();
      setExitClearTop((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));

      if (bubbleDragPos || bubbleDragging) return;
      const bubble = bubbleElRef.current;
      if (!bubble || !exitEl) return;
      // 시작/완료 히어로 카드는 처음부터 종료 버튼 아래 상단 고정이라 exit-safe 재배치가 불필요
      // (다시 잡으면 maxHeight·overflow 스타일이 빠지며 한 번 더 튈 수 있음)
      if (
        bubble.classList.contains('cp-tutorial-bubble--intro') ||
        bubble.classList.contains('cp-tutorial-bubble--celebration')
      ) {
        return;
      }
      const b = clientRectToLayoutRect(bubble.getBoundingClientRect());
      const e = clientRectToLayoutRect(exitEl.getBoundingClientRect());
      if (b.height <= 0 || e.height <= 0) return;
      const neededTop = e.bottom + BUBBLE_GAP;
      if (b.top < neededTop - 0.5) {
        setBubbleExitSafePos((prev) => {
          // 중앙 카드는 측정 left로 바꾸면 translate(-50%) 중앙 정렬이 깨져
          // 화면 왼쪽으로 밀려 보인다. 좌우는 중앙 유지, 세로만 내린다.
          const keepCenteredX = bubble.classList.contains('cp-tutorial-bubble--center');
          const left = keepCenteredX ? 0 : b.left;
          if (
            prev &&
            Math.abs(prev.top - neededTop) < 0.5 &&
            Boolean(prev.centerX) === keepCenteredX &&
            (keepCenteredX || Math.abs(prev.left - left) < 0.5)
          ) {
            return prev;
          }
          return { left, top: neededTop, centerX: keepCenteredX };
        });
      }
    };
    update();
    const timer = window.setInterval(update, POLL_INTERVAL_MS);
    window.addEventListener('resize', update);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, update as EventListener);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, update as EventListener);
    };
  }, [activeTutorial, stepKey, bubbleDragPos, bubbleDragging, holes, unionRect, pageZoom]);
  /**
   * `manualAdvanceIfExists`가 있으면, 해당 셀렉터가 화면에 있는지로
   * "다음" 버튼 노출 여부(선택 사항 vs 강제 클릭)를 동적으로 정한다.
   * 예: 그룹/멤버가 이미 하나 이상 있으면 건너뛸 수 있게, 없으면 클릭을 강제한다.
   */
  const effectiveManualAdvance = step?.manualAdvanceIfExists
    ? Boolean(manualAdvanceOverride)
    : Boolean(step?.manualAdvance);
  /** "다음" 버튼이 실제로 눌릴 수 있는지(`manualAdvanceEnableWhen` 게이트). */
  const manualAdvanceButtonEnabled =
    !step?.manualAdvanceEnableWhen || Boolean(manualAdvanceEnableReady);

  React.useEffect(() => {
    const cond = step?.manualAdvanceIfExists;
    if (!cond) {
      setManualAdvanceOverride(null);
      return undefined;
    }
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setManualAdvanceOverride(resolveTutorialTargets(cond).length > 0);
      window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  React.useEffect(() => {
    const cond = step?.manualAdvanceEnableWhen;
    if (!cond) {
      setManualAdvanceEnableReady(null);
      return undefined;
    }
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setManualAdvanceEnableReady(resolveTutorialTargets(cond).length > 0);
      window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  React.useEffect(() => {
    setTargetEls([]);
    setHoles([]);
    setUnionRect(null);
    setTargetMissing(false);
    missingSinceRef.current = null;
    scrolledRef.current = false;
    resetTutorialPageScrollToTop();
    if (!step?.targetSelector) {
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    let cancelled = false;
    const selector = step.targetSelector;
    const includeStacked = !step.blockTargetClick;
    const tick = () => {
      if (cancelled) return;
      const found = resolveTutorialTargets(selector);
      if (found.length > 0) {
        missingSinceRef.current = null;
        const next = expandTutorialTargetsWithStackedOverlays(found, { includeStacked });
        setTargetEls((prev) => (sameTargetEls(prev, next) ? prev : next));
        setSearching(false);
        setTargetMissing(false);
      } else {
        setTargetEls((prev) => (prev.length === 0 ? prev : []));
        if (missingSinceRef.current == null) missingSinceRef.current = Date.now();
        const missingFor = Date.now() - missingSinceRef.current;
        if (missingFor >= RECOVER_HINT_MS) {
          setSearching(false);
          setTargetMissing(true);
        } else {
          setSearching(true);
          setTargetMissing(false);
        }
      }
      window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  React.useEffect(() => {
    if (targetEls.length === 0) return undefined;
    if (!scrolledRef.current) {
      scrolledRef.current = true;
      try {
        revealTutorialTargetPreferringPageTop(targetEls[0]);
      } catch {
        /* noop */
      }
    }
    let raf = 0;
    let alive = true;
    const measure = () => {
      if (!alive) return;
      const connected = targetEls.filter((el) => el.isConnected);
      if (connected.length === 0) {
        setTargetEls([]);
        setHoles([]);
        setUnionRect(null);
        return;
      }
      if (connected.length !== targetEls.length) {
        setTargetEls(connected);
      }
      const holePadding = step?.spotlightSeparate ? HOLE_PADDING_SEPARATE : HOLE_PADDING;
      let nextHoles = measureTutorialSpotlightHoles(connected, holePadding, {
        mergeNearby: !step?.spotlightSeparate,
      });
      if (step?.spotlightUnion) {
        nextHoles = unionAllTutorialSpotlightHoles(nextHoles);
      }
      setHoles((prev) => (holesEqual(prev, nextHoles) ? prev : nextHoles));
      setUnionRect((prev) => {
        const next = unionTutorialTargetRect(connected);
        if (!next) return null;
        if (
          prev &&
          Math.abs(prev.top - next.top) < 0.5 &&
          Math.abs(prev.left - next.left) < 0.5 &&
          Math.abs(prev.width - next.width) < 0.5 &&
          Math.abs(prev.height - next.height) < 0.5
        ) {
          return prev;
        }
        return next;
      });
      raf = window.requestAnimationFrame(measure);
    };
    raf = window.requestAnimationFrame(measure);
    return () => {
      alive = false;
      window.cancelAnimationFrame(raf);
    };
  }, [targetEls, step?.spotlightUnion, step?.spotlightSeparate, pageZoom]);

  /**
   * 후속 안내(followUp) 감지: 이 단계의 버튼을 눌러 확인 모달 등이 열리면 targetSelector가 매칭된다.
   * 모달이 열리면 하이라이트가 후속 대상으로 전환되고, 닫히면 원래 단계 하이라이트로 되돌아간다.
   * 다음 실제 단계로는 자동으로 넘어가지 않으며, 말풍선의 "다음" 버튼을 눌러야만 진행된다.
   * (같은 단계에서 버튼을 여러 번 눌러 후속 대상을 다시 열 수도 있다.)
   */
  React.useEffect(() => {
    setFollowUpEls([]);
    setFollowUpHoles([]);
    setFollowUpUnionRect(null);
    followUpWasActiveRef.current = false;
    const followUp = step?.followUp;
    if (!followUp) return undefined;
    let cancelled = false;
    const selector = followUp.targetSelector;
    const includeStacked = !followUp.blockTargetClick;
    const tick = () => {
      if (cancelled) return;
      const found = resolveTutorialTargets(selector);
      if (found.length > 0) {
        followUpWasActiveRef.current = true;
        const next = expandTutorialTargetsWithStackedOverlays(found, { includeStacked });
        setFollowUpEls((prev) => (sameTargetEls(prev, next) ? prev : next));
      } else if (followUpWasActiveRef.current) {
        followUpWasActiveRef.current = false;
        setFollowUpEls([]);
      }
      window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    const timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  React.useEffect(() => {
    if (followUpEls.length === 0) return undefined;
    let raf = 0;
    let alive = true;
    const measure = () => {
      if (!alive) return;
      const connected = followUpEls.filter((el) => el.isConnected);
      if (connected.length === 0) {
        setFollowUpHoles([]);
        setFollowUpUnionRect(null);
        return;
      }
      const nextHoles = measureTutorialSpotlightHoles(connected, HOLE_PADDING);
      setFollowUpHoles((prev) => (holesEqual(prev, nextHoles) ? prev : nextHoles));
      setFollowUpUnionRect((prev) => {
        const next = unionTutorialTargetRect(connected);
        if (!next) return null;
        if (
          prev &&
          Math.abs(prev.top - next.top) < 0.5 &&
          Math.abs(prev.left - next.left) < 0.5 &&
          Math.abs(prev.width - next.width) < 0.5 &&
          Math.abs(prev.height - next.height) < 0.5
        ) {
          return prev;
        }
        return next;
      });
      raf = window.requestAnimationFrame(measure);
    };
    raf = window.requestAnimationFrame(measure);
    return () => {
      alive = false;
      window.cancelAnimationFrame(raf);
    };
  }, [followUpEls, pageZoom]);

  /**
   * "저장하지 않은 변경사항" 확인 모달 감지: 현재 단계와 무관하게 화면 어디서든 뜰 수 있으므로
   * stepKey가 아니라 튜토리얼 활성 여부에만 의존해 계속 감시한다. 뜨면 항상 하이라이트·조작
   * 가능하게 하고, 닫히면 원래 단계 표시로 되돌아간다.
   */
  React.useEffect(() => {
    if (!activeTutorial) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const found = resolveTutorialTargets(TUTORIAL_LEAVE_CONFIRM_SELECTOR);
      if (found.length > 0) {
        leaveConfirmWasActiveRef.current = true;
        setLeaveConfirmEls((prev) => (sameTargetEls(prev, found) ? prev : found));
      } else if (leaveConfirmWasActiveRef.current) {
        leaveConfirmWasActiveRef.current = false;
        setLeaveConfirmEls([]);
      }
      window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    const timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(activeTutorial)]);

  React.useEffect(() => {
    if (leaveConfirmEls.length === 0) {
      setLeaveConfirmHoles([]);
      setLeaveConfirmUnionRect(null);
      return undefined;
    }
    let raf = 0;
    let alive = true;
    const measure = () => {
      if (!alive) return;
      const connected = leaveConfirmEls.filter((el) => el.isConnected);
      if (connected.length === 0) {
        setLeaveConfirmHoles([]);
        setLeaveConfirmUnionRect(null);
        return;
      }
      const nextHoles = measureTutorialSpotlightHoles(connected, HOLE_PADDING);
      setLeaveConfirmHoles((prev) => (holesEqual(prev, nextHoles) ? prev : nextHoles));
      setLeaveConfirmUnionRect((prev) => {
        const next = unionTutorialTargetRect(connected);
        if (!next) return null;
        if (
          prev &&
          Math.abs(prev.top - next.top) < 0.5 &&
          Math.abs(prev.left - next.left) < 0.5 &&
          Math.abs(prev.width - next.width) < 0.5 &&
          Math.abs(prev.height - next.height) < 0.5
        ) {
          return prev;
        }
        return next;
      });
      raf = window.requestAnimationFrame(measure);
    };
    raf = window.requestAnimationFrame(measure);
    return () => {
      alive = false;
      window.cancelAnimationFrame(raf);
    };
  }, [leaveConfirmEls, pageZoom]);

  React.useEffect(() => {
    // manualAdvance여도 advanceSelector가 있으면 그 클릭(모달 닫힘 등)으로 다음 단계 진행을 허용한다.
    // 예: 학습내용 "데이터 가져오기" 후 모달이 닫히면 자동으로 넘어가고, 건너뛰기는 말풍선 "다음"으로.
    if (!step?.targetSelector || step.followUp) {
      return undefined;
    }
    if (effectiveManualAdvance && !step.advanceSelector) {
      return undefined;
    }
    const targetSelector = step.targetSelector;
    const advanceSelector = step.advanceSelector;
    const advanceWhenExists = step.advanceWhenSelectorExists;
    const advanceImmediately = Boolean(step.advanceImmediately);
    const onDocClick = (e: MouseEvent) => {
      const advanceEls = advanceSelector
        ? resolveTutorialTargets(advanceSelector)
        : resolveTutorialTargets(targetSelector);
      if (advanceEls.length === 0) return;
      if (!isTutorialClickOnTarget(advanceEls, e.target)) return;
      // advanceSelector(예: 모달 "저장")는 클릭만으로 완료가 아닐 수 있다.
      // 검증 실패로 모달이 그대로면 진행하지 않고, 타깃이 사라진 뒤에만 다음 단계로 넘긴다.
      // (대기는 advanceWaitRef에 두어 targetEls 변동으로 effect가 재실행돼도 취소되지 않게 한다.)
      // advanceImmediately: "등록"처럼 클릭 후에도 하이라이트 영역이 남는 버튼은 바로 진행.
      // 기본(advanceSelector만): 모달 저장처럼 타깃이 사라진 뒤에만 진행.
      // 웹문서 생성처럼 클릭 후 다른 모달이 열리는 경우는 advanceWhenSelectorExists가 뜨면 진행.
      if (advanceSelector) {
        if (advanceImmediately) {
          scheduleTryAdvanceStepRef.current(60);
          return;
        }
        if (advanceWaitRef.current) {
          advanceWaitRef.current.cancelled = true;
          if (advanceWaitRef.current.timer != null) window.clearTimeout(advanceWaitRef.current.timer);
        }
        const waitState = { cancelled: false, timer: null as number | null };
        advanceWaitRef.current = waitState;
        const started = Date.now();
        const keyAtClick = stepKeyRef.current;
        const waitGone = () => {
          if (waitState.cancelled) return;
          if (resolveTutorialTargets(targetSelector).length === 0) {
            tryAdvanceStepRef.current(keyAtClick);
            return;
          }
          if (advanceWhenExists && tutorialSelectorExistsInDom(advanceWhenExists)) {
            tryAdvanceStepRef.current(keyAtClick);
            return;
          }
          if (Date.now() - started > 8000) return;
          waitState.timer = window.setTimeout(waitGone, 100);
        };
        waitState.timer = window.setTimeout(waitGone, 80);
        return;
      }
      scheduleTryAdvanceStepRef.current(60);
    };
    document.addEventListener('click', onDocClick, true);
    return () => {
      document.removeEventListener('click', onDocClick, true);
    };
  }, [
    stepKey,
    step?.targetSelector,
    step?.advanceSelector,
    step?.advanceWhenSelectorExists,
    step?.advanceImmediately,
    effectiveManualAdvance,
    step?.followUp,
  ]);

  /**
   * followUp(중첩 모달) 안에서 `advanceSelector`로 지정한 요소를 실제로 클릭하면,
   * 그 클릭으로 앱의 실제 동작(예: 또 다른 모달 열기)이 일어남과 동시에
   * 튜토리얼 전체 단계도 다음으로 진행한다. 모달 설명을 세분화된 단계로 나눌 때 사용.
   */
  React.useEffect(() => {
    const advanceSelector = step?.followUp?.advanceSelector;
    if (!advanceSelector || followUpEls.length === 0) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const advanceEls = resolveTutorialTargets(advanceSelector);
      if (advanceEls.length === 0) return;
      if (isTutorialClickOnTarget(advanceEls, e.target)) {
        scheduleTryAdvanceStepRef.current(60);
      }
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [followUpEls, step?.followUp?.advanceSelector]);

  /** 탐색 단축키(Shift+F 등)로 이동한 경우에도 다음 단계로 진행 */
  React.useEffect(() => {
    const shortcut = step?.advanceShortcut;
    if (!shortcut || effectiveManualAdvance || step?.followUp) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (shortcut.shift && !e.shiftKey) return;
      if (e.key.length !== 1) return;
      if (e.key.toLowerCase() !== shortcut.key.toLowerCase()) return;
      // 실제 라우트 이동이 일어난 뒤에 넘긴다 (선택 없어 이동 실패하면 path 유지)
      const keyAtSchedule = stepKeyRef.current;
      window.setTimeout(() => {
        if (window.location.pathname !== stepStartPathRef.current) {
          tryAdvanceStepRef.current(keyAtSchedule);
        }
      }, 180);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [step?.advanceShortcut, step?.followUp, effectiveManualAdvance, stepKey]);

  /** 탐색으로 목적 경로에 도착하면(클릭·단축키 공통) 다음 단계로 진행 */
  React.useEffect(() => {
    const prefixes = step?.advanceOnPathPrefix;
    if (!prefixes || effectiveManualAdvance || step?.followUp) return;
    const list = typeof prefixes === 'string' ? [prefixes] : [...prefixes];
    const path = location.pathname;
    if (!path || path === stepStartPathRef.current) return;
    const matched = list.some((prefix) => path === prefix || path.startsWith(prefix));
    if (matched) tryAdvanceStepRef.current();
  }, [location.pathname, step?.advanceOnPathPrefix, step?.followUp, effectiveManualAdvance, stepKey]);

  /**
   * 지정 셀렉터(모달 등)가 나타나면 클릭 진행이 누락돼도 다음 단계로 넘긴다.
   * 예: "학습내용 가져오기" 클릭 후 방식 선택 모달이 열린 경우.
   * tryAdvanceStep 참조 변경으로 폴링이 끊기지 않도록 ref로 호출한다.
   */
  React.useEffect(() => {
    const sel = step?.advanceWhenSelectorExists;
    // manualAdvance여도(건너뛰기용 "다음") 셀렉터가 나타나면 자동 진행한다.
    // 예: 보상 지급 버튼이 없을 때는 "다음"으로 건너뛰고, 있으면 눌러 모달이 열릴 때 넘어감.
    if (!sel || step?.followUp) return undefined;
    let cancelled = false;
    let pollTimer: number | null = null;
    const keyAtStart = stepKey;
    const tick = () => {
      if (cancelled) return;
      // 하이라이트용 가시성 필터에 걸려도, DOM에 모달이 있으면 진행한다.
      if (tutorialSelectorExistsInDom(sel) || resolveTutorialTargets(sel).length > 0) {
        tryAdvanceStepRef.current(keyAtStart);
        return;
      }
      pollTimer = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    // 단계 진입 직후(이미 대상이 떠 있는 경우)도 바로 검사한다.
    pollTimer = window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
      if (pollTimer != null) window.clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, step?.advanceWhenSelectorExists, step?.followUp]);

  /**
   * 지정 셀렉터(모달 등)가 한 번 보인 뒤 사라지면 다음 단계로 넘긴다.
   * 예: 모달 설명 단계에서 X·백드롭으로 닫아도 말풍선 "다음"과 같이 진행.
   */
  React.useEffect(() => {
    const sel = step?.advanceWhenSelectorGone;
    if (!sel || step?.followUp) return undefined;
    // 단계 진입 시 이미 모달이 열려 있으면 바로 seen 처리 (이전 단계에서 연 경우).
    if (resolveTutorialTargets(sel).length > 0) {
      advanceGoneSeenRef.current = true;
    }
    let cancelled = false;
    let pollTimer: number | null = null;
    const keyAtStart = stepKey;
    const tick = () => {
      if (cancelled) return;
      const exists = resolveTutorialTargets(sel).length > 0;
      if (exists) {
        advanceGoneSeenRef.current = true;
      } else if (advanceGoneSeenRef.current) {
        tryAdvanceStepRef.current(keyAtStart);
        return;
      }
      pollTimer = window.setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimer = window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
      if (pollTimer != null) window.clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, step?.advanceWhenSelectorGone, step?.followUp]);

  /** 모달 자동 닫기처럼 튜토리얼이 직접 수행하는 프로그램적 클릭 동안 클릭 가드를 잠시 우회한다. */
  const internalActionRef = React.useRef(false);

  /**
   * 튜토리얼 클릭 화이트리스트(기본: 하이라이트 밖 클릭은 모두 차단).
   * 1) 이동·모달 열기 등 실제 클릭이 필요한 단계는 하이라이트된 대상만 클릭을 허용한다.
   * 2) 기능만 설명하는 단계(`blockTargetClick`)는 하이라이트도 클릭을 막고, 말풍선 "다음"으로만 진행한다.
   * 3) followUp(중첩 모달 등) 표시 중에는 그 대상 기준으로 다시 판단한다.
   *    (중첩으로 열리는 모달·팝업은 `expandTutorialTargetsWithStackedOverlays`가 targetEls/followUpEls에 자동 포함)
   * 튜토리얼 자체 UI(말풍선·종료)는 항상 허용한다. 타깃을 못 찾은 동안에는 화면 이동을 위해 클릭을 모두 허용한다.
   */
  React.useEffect(() => {
    if (!activeTutorial || !step) return undefined;
    const sectionCardOpen =
      Boolean(step.section) &&
      step.presentation !== 'intro' &&
      step.presentation !== 'celebration' &&
      sectionCardDismissedKey !== stepKey;
    const isAllowedInteraction = (raw: EventTarget | null): 'allow' | 'block' | 'nested' => {
      if (!(raw instanceof Node)) return 'block';
      const el = raw instanceof Element ? raw : raw.parentElement;
      if (el?.closest('.cp-tutorial-root')) return 'allow';
      // 구간 소타이틀 카드가 떠 있는 동안에는 화면 조작을 막는다.
      if (sectionCardOpen) return 'block';
      if (leaveConfirmEls.length > 0 && isTutorialClickOnTarget(leaveConfirmEls, raw)) return 'allow';
      if (
        followUpEls.length === 0 &&
        leaveConfirmEls.length === 0 &&
        step.targetSelector &&
        (targetMissing || targetEls.length === 0)
      ) {
        return 'allow';
      }

      const followUpNow = followUpEls.length > 0;
      let allowed: HTMLElement[] = [];
      let blockCloseButtons = false;
      const primarySelector = followUpNow ? step.followUp?.targetSelector : step.targetSelector;
      const primaryEls = primarySelector ? resolveTutorialTargets(primarySelector) : [];
      if (followUpNow) {
        if (!step.followUp?.blockTargetClick) allowed = followUpEls;
        blockCloseButtons =
          Boolean(step.followUp?.manualAdvance) && !step.followUp?.allowManualAdvanceActions;
      } else if (step.targetSelector && !step.blockTargetClick) {
        // 하이라이트된 요소만 허용(모달 전체가 아니라 파트 셀렉터 기준).
        // stacked 확장은 하이라이트 표시용이며, 클릭은 primary(+중첩 오버레이)만 허용한다.
        allowed = primaryEls.length > 0 ? primaryEls : targetEls;
        blockCloseButtons =
          (Boolean(step.manualAdvance) || Boolean(step.advanceSelector)) &&
          !step.allowManualAdvanceActions;
      }

      // 주 타깃보다 위에 쌓인 중첩 오버레이만 허용(뒤에 가려진 미니 모달은 제외)
      const nestedEls = getTutorialStackedOverlaysAbove(primaryEls);
      if (nestedEls.some((n) => n === raw || n.contains(raw))) return 'nested';
      if (el) {
        const backdrop = el.closest(TUTORIAL_NESTED_OVERLAY_BACKDROP_SELECTOR);
        if (backdrop) {
          const wrapsNested = nestedEls.some((n) => backdrop.contains(n));
          const wrapsPrimary = primaryEls.some((p) => backdrop.contains(p));
          if (wrapsNested && !wrapsPrimary) return 'nested';
        }
      }

      if (!followUpNow && nestedEls.length > 0 && !step.blockTargetClick) {
        allowed = [...allowed, ...nestedEls];
      }

      if (allowed.length > 0 && isTutorialClickOnTarget(allowed, raw)) {
        const blockClickSel = !followUpNow ? step.blockClickSelectors : undefined;
        if (blockClickSel && el) {
          const blockedEls = resolveTutorialTargets(blockClickSel);
          if (blockedEls.length > 0 && isTutorialClickOnTarget(blockedEls, raw)) {
            return 'block';
          }
          // resolve에 안 잡혀도 closest로 잠금 셀렉터 매칭.
          // `text:라벨@base`·쉼표 목록은 Element.closest에 넣을 수 없으므로 CSS 조각만 시도한다.
          for (const part of splitTutorialSelectors(blockClickSel)) {
            if (!part || part.startsWith('text:')) continue;
            try {
              if (el.closest(part)) return 'block';
            } catch {
              /* invalid CSS fragment — ignore */
            }
          }
        }
        if (blockCloseButtons && el?.closest(TUTORIAL_MANUAL_ADVANCE_CLOSE_BLOCK_SELECTOR)) {
          const advanceSel = followUpNow ? step.followUp?.advanceSelector : step.advanceSelector;
          if (advanceSel) {
            const advanceEls = resolveTutorialTargets(advanceSel);
            if (advanceEls.length > 0 && isTutorialClickOnTarget(advanceEls, raw)) {
              return 'allow';
            }
          }
          const closeEl = el.closest(TUTORIAL_MANUAL_ADVANCE_CLOSE_BLOCK_SELECTOR);
          // 하이라이트 타깃 자체가 cp-btn-secondary인 경우(기초시간표 반영 등)는 닫기 차단에서 제외
          if (closeEl && allowed.some((p) => p === closeEl)) {
            return 'allow';
          }
          const closesPrimary = Boolean(
            closeEl && primaryEls.some((p) => p === closeEl || p.contains(closeEl))
          );
          if (closesPrimary) return 'block';
          return 'allow';
        }
        return 'allow';
      }
      return 'block';
    };

    const guard = (e: Event) => {
      if (internalActionRef.current) return;
      const verdict = isAllowedInteraction(e.target);
      if (verdict === 'allow' || verdict === 'nested') return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    const focusGuard = (e: FocusEvent) => {
      if (internalActionRef.current) return;
      const verdict = isAllowedInteraction(e.target);
      if (verdict === 'allow' || verdict === 'nested') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.target instanceof HTMLElement) e.target.blur();
    };

    document.addEventListener('click', guard, true);
    document.addEventListener('mousedown', guard, true);
    document.addEventListener('pointerdown', guard, true);
    document.addEventListener('dblclick', guard, true);
    document.addEventListener('focusin', focusGuard, true);
    return () => {
      document.removeEventListener('click', guard, true);
      document.removeEventListener('mousedown', guard, true);
      document.removeEventListener('pointerdown', guard, true);
      document.removeEventListener('dblclick', guard, true);
      document.removeEventListener('focusin', focusGuard, true);
    };
  }, [activeTutorial, step, stepKey, sectionCardDismissedKey, targetEls, followUpEls, leaveConfirmEls, targetMissing]);

  const clampBubblePos = React.useCallback((left: number, top: number) => {
    const { width: vw, height: vh } = getLayoutViewportSize();
    const el = bubbleElRef.current;
    const presentation = step?.presentation;
    const card =
      presentation === 'celebration' || presentation === 'intro'
        ? 'hero'
        : step?.section && sectionCardDismissedKey !== stepKey
          ? 'section'
          : 'default';
    const w =
      el?.offsetWidth ||
      (card === 'hero'
        ? BUBBLE_CELEBRATION_WIDTH
        : card === 'section'
          ? BUBBLE_SECTION_WIDTH
          : BUBBLE_WIDTH);
    const h =
      el?.offsetHeight ||
      (card === 'hero'
        ? BUBBLE_CELEBRATION_EST_HEIGHT
        : card === 'section'
          ? BUBBLE_SECTION_EST_HEIGHT
          : BUBBLE_EST_HEIGHT);
    return {
      left: Math.min(Math.max(left, BUBBLE_MARGIN), Math.max(BUBBLE_MARGIN, vw - w - BUBBLE_MARGIN)),
      top: Math.min(Math.max(top, BUBBLE_MARGIN), Math.max(BUBBLE_MARGIN, vh - h - BUBBLE_MARGIN)),
    };
  }, [sectionCardDismissedKey, step?.presentation, step?.section, stepKey, pageZoom]);

  const handleBubblePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      // 버튼·링크 등 조작 요소에서는 드래그를 시작하지 않는다.
      if (target.closest('button, a, input, select, textarea, label')) return;
      const bubbleEl = bubbleElRef.current;
      if (!bubbleEl) return;
      const rect = clientRectToLayoutRect(bubbleEl.getBoundingClientRect());
      const start = clientPointToLayoutPoint(e.clientX, e.clientY);
      // 클릭만 한 경우 is-dragging을 켜지 않는다.
      // (잠깐 켜졌다 꺼지면 등장 애니메이션이 다시 재생되어 깜박임)
      bubbleDragRef.current = {
        pointerId: e.pointerId,
        startX: start.x,
        startY: start.y,
        originLeft: rect.left,
        originTop: rect.top,
        active: false,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    },
    []
  );

  const handleBubblePointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = bubbleDragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const point = clientPointToLayoutPoint(e.clientX, e.clientY);
      const dx = point.x - drag.startX;
      const dy = point.y - drag.startY;
      if (!drag.active) {
        // 수 픽셀 이상 움직였을 때만 드래그로 간주
        if (dx * dx + dy * dy < 25) return;
        drag.active = true;
        setBubbleDragging(true);
      }
      const next = clampBubblePos(drag.originLeft + dx, drag.originTop + dy);
      setBubbleDragPos(next);
    },
    [clampBubblePos]
  );

  const endBubbleDrag = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = bubbleDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const wasActive = drag.active;
    bubbleDragRef.current = null;
    if (wasActive) setBubbleDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }, []);

  if (!activeTutorial || !step) return null;

  const leaveConfirmActive = leaveConfirmEls.length > 0;
  const followUpActive = followUpEls.length > 0;
  const followUp = step.followUp;
  const followUpManualAdvance = Boolean(followUpActive && followUp?.manualAdvance);
  const isIntro = !leaveConfirmActive && !followUpActive && step.presentation === 'intro';
  const isCelebration = !leaveConfirmActive && !followUpActive && step.presentation === 'celebration';
  const wantsSectionCard =
    Boolean(step.section) && step.presentation !== 'intro' && step.presentation !== 'celebration';
  const showSectionCard =
    !leaveConfirmActive &&
    !followUpActive &&
    wantsSectionCard &&
    sectionCardDismissedKey !== stepKey;
  const isHeroCard = isIntro || isCelebration;
  const isCardBubble = isHeroCard || showSectionCard;
  const sectionTitle = step.section ? formatTutorialSectionTitle(step.section) : '';
  const cardTitle = isIntro
    ? step.cardTitle || '시작해요!'
    : isCelebration
      ? step.cardTitle || '축하합니다!'
      : sectionTitle;
  const displayMascot = leaveConfirmActive
    ? TUTORIAL_LEAVE_CONFIRM_MASCOT
    : followUpActive && followUp
      ? followUp.mascot
      : showSectionCard
        ? step.sectionMascot || `이제 「${sectionTitle}」 구간을 진행해요. 준비되면 다음을 눌러 주세요.`
        : step.mascot;
  const displayHoles = showSectionCard
    ? []
    : leaveConfirmActive
      ? leaveConfirmHoles
      : followUpActive
        ? followUpHoles
        : holes;
  const displayPlacementPref = leaveConfirmActive
    ? undefined
    : followUpActive
      ? followUp?.placement
      : isCardBubble
        ? 'center'
        : step.placement;
  const bubbleWidth = isHeroCard
    ? BUBBLE_CELEBRATION_WIDTH
    : showSectionCard
      ? BUBBLE_SECTION_WIDTH
      : BUBBLE_WIDTH;

  const bubbleAnchor =
    displayHoles.length > 0
      ? {
          top: Math.min(...displayHoles.map((h) => h.top)),
          left: Math.min(...displayHoles.map((h) => h.left)),
          width:
            Math.max(...displayHoles.map((h) => h.left + h.width)) -
            Math.min(...displayHoles.map((h) => h.left)),
          height:
            Math.max(...displayHoles.map((h) => h.top + h.height)) -
            Math.min(...displayHoles.map((h) => h.top)),
        }
      : leaveConfirmActive
        ? leaveConfirmUnionRect
        : followUpActive
          ? followUpUnionRect
          : unionRect;

  const placement = resolvePlacement(displayPlacementPref, bubbleAnchor);
  const autoBubbleStyle = computeBubbleStyle(
    placement,
    bubbleAnchor,
    bubbleWidth,
    exitClearTop
  );
  const bubbleStyle: React.CSSProperties = bubbleDragPos
    ? {
        left: bubbleDragPos.left,
        top: bubbleDragPos.top,
        right: 'auto',
        bottom: 'auto',
        width: bubbleWidth,
        transform: 'none',
      }
    : bubbleExitSafePos
      ? bubbleExitSafePos.centerX
        ? {
            left: '50%',
            top: bubbleExitSafePos.top,
            right: 'auto',
            bottom: 'auto',
            width: bubbleWidth,
            transform: 'translateX(-50%)',
          }
        : {
            left: bubbleExitSafePos.left,
            top: bubbleExitSafePos.top,
            right: 'auto',
            bottom: 'auto',
            width: bubbleWidth,
            transform: 'none',
          }
      : autoBubbleStyle;

  const showBubbleTail =
    !bubbleDragPos &&
    placement !== 'center' &&
    placement !== 'below-nav' &&
    isBubbleTailNearHighlight(
      placement,
      bubbleAnchor,
      bubbleStyle,
      bubbleWidth,
      isCardBubble
        ? isHeroCard
          ? BUBBLE_CELEBRATION_EST_HEIGHT
          : BUBBLE_SECTION_EST_HEIGHT
        : BUBBLE_EST_HEIGHT
    );

  const isLast = activeStepIndex === totalSteps - 1;
  const isNarrationStep = !step.targetSelector;
  const showManualButton =
    showSectionCard ||
    (!leaveConfirmActive &&
      (followUpManualAdvance || (!followUpActive && (isNarrationStep || effectiveManualAdvance))));
  const primaryLabel = showSectionCard
    ? '다음'
    : step.actionLabel || (isLast ? '완료' : '다음');
  const hasHoles = leaveConfirmActive
    ? displayHoles.length > 0
    : followUpActive
      ? displayHoles.length > 0
      : !showSectionCard && Boolean(step.targetSelector) && holes.length > 0;
  const blockClicks =
    hasHoles &&
    !leaveConfirmActive &&
    !showSectionCard &&
    (followUpActive
      ? Boolean(followUp?.blockTargetClick)
      : Boolean(step.blockTargetClick));

  const handlePrimaryAdvance = () => {
    if (showSectionCard && stepKey) {
      setSectionCardDismissedKey(stepKey);
      setBubbleDragPos(null);
      setBubbleAnimationDone(false);
      return;
    }
    if (!manualAdvanceButtonEnabled) return;
    // "다음"으로 넘기기 전 지정 버튼 클릭(웹캠 끄기 등)
    if (!followUpActive && step?.clickOnManualAdvanceSelectors) {
      const clickEls = resolveTutorialTargets(step.clickOnManualAdvanceSelectors);
      if (clickEls.length > 0) {
        internalActionRef.current = true;
        clickEls.forEach((el) => {
          try {
            el.click();
          } catch {
            /* ignore */
          }
        });
        window.setTimeout(() => {
          internalActionRef.current = false;
        }, 120);
      }
    }
    const skipToId = followUpActive
      ? followUp?.manualAdvanceSkipToStepId
      : step?.manualAdvanceSkipToStepId;
    const skipIdx =
      skipToId && activeTutorial ? activeTutorial.steps.findIndex((s) => s.id === skipToId) : -1;
    // 설명 중이던 모달·팝업이 열려 있는데 다음 단계에서 더 이상 필요하지 않다면 자동으로 닫는다.
    // (모달마다 닫히는 방식이 달라 전용 버튼 → 바깥(backdrop) 클릭 → Escape 순으로 시도)
    const nextStep =
      skipIdx >= 0 && activeTutorial
        ? activeTutorial.steps[skipIdx]
        : (activeTutorial?.steps[activeStepIndex + 1] ?? null);
    const neededEls = nextStep?.targetSelector ? resolveTutorialTargets(nextStep.targetSelector) : [];
    const openOverlays = resolveTutorialTargets(TUTORIAL_STACKED_OVERLAY_SELECTOR);
    // 다음 단계 타깃을 아직 DOM에서 못 찾으면(중첩 모달 안 세부 셀렉터 등) 열려 있는 모달을 닫지 않는다.
    // 또한 필요한 오버레이 "아래"에 깔린 부모 모달(예: 평가계획 등록 → 기준안 웹문서)은 닫지 않는다.
    // (부모를 닫으면 자식 에디터 state까지 같이 사라져 설명 중 모달이 꺼진다.)
    if (openOverlays.length > 0 && !(nextStep?.targetSelector && neededEls.length === 0)) {
      const neededOverlays = openOverlays.filter((el) =>
        neededEls.some((needed) => needed === el || el.contains(needed) || needed.contains(el))
      );
      const leftoverOverlays = openOverlays.filter((el) => {
        if (neededOverlays.some((needed) => needed === el)) return false;
        const underNeededStack = neededOverlays.some(
          (needed) =>
            (el.compareDocumentPosition(needed) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
        );
        return !underNeededStack;
      });
      if (leftoverOverlays.length > 0) {
        internalActionRef.current = true;
        closeTutorialOverlayElements(leftoverOverlays);
        // 닫기 직후 뜨는 변경사항 확인 모달도 저장 없이 이어서 닫는다.
        const dismissFollowUpConfirms = () => {
          dismissTutorialUnsavedConfirmOverlays();
          // 한 번 더: 부모 모달 닫힘 → 확인창이 늦게 마운트되는 경우
          window.setTimeout(() => {
            dismissTutorialUnsavedConfirmOverlays();
            internalActionRef.current = false;
          }, 220);
        };
        window.setTimeout(dismissFollowUpConfirms, 80);
      }
    }
    if (skipToId && skipIdx >= 0) {
      goToStepById(skipToId);
      return;
    }
    if (step?.advanceGoToStepId) {
      goToStepById(step.advanceGoToStepId);
      return;
    }
    goToNextStep();
  };

  const showTargetMissingUi =
    targetMissing && !followUpActive && !leaveConfirmActive && !showSectionCard && !isCardBubble;
  const targetMissingHintText = showTargetMissingUi
    ? buildTutorialTargetMissingHint(step.recover, step.targetMissingHint)
    : '';
  /** 타깃을 못 찾았거나 찾는 중이면 딤을 걷어 사용자가 직접 이전 화면으로 돌아갈 수 있게 한다. */
  const hideDimForNavigation =
    !showSectionCard &&
    !isCardBubble &&
    (showTargetMissingUi ||
      (searching && Boolean(step.targetSelector) && !followUpActive && !leaveConfirmActive));

  return createPortal(
    <div className="cp-tutorial-root" role="dialog" aria-modal="true" aria-label={`${activeTutorial.title} 튜토리얼`}>
      {isCelebration && stepKey ? <TutorialCelebrationFireworks animKey={stepKey} /> : null}
      {hasHoles && !hideDimForNavigation ? (
        <>
          <svg className="cp-tutorial-dim-svg" width="100%" height="100%" aria-hidden>
            <defs>
              <mask
                id="cp-tutorial-spotlight-mask"
                maskUnits="userSpaceOnUse"
                maskContentUnits="userSpaceOnUse"
              >
                <rect x="0" y="0" width="10000" height="10000" fill="#fff" />
                {displayHoles.map((h, i) => (
                  <rect
                    key={`hole-${i}`}
                    x={h.left}
                    y={h.top}
                    width={h.width}
                    height={h.height}
                    rx={h.radius}
                    ry={h.radius}
                    fill="#000"
                  />
                ))}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="10000"
              height="10000"
              fill="rgba(12, 14, 22, 0.62)"
              mask="url(#cp-tutorial-spotlight-mask)"
            />
          </svg>
          {displayHoles.map((h, i) => (
            <div
              key={`ring-${i}`}
              className="cp-tutorial-spotlight-ring"
              style={{
                top: h.top,
                left: h.left,
                width: h.width,
                height: h.height,
                borderRadius: h.radius,
              }}
            />
          ))}
          {blockClicks
            ? displayHoles.map((h, i) => (
                <div
                  key={`block-${i}`}
                  className="cp-tutorial-click-block"
                  style={{
                    top: h.top,
                    left: h.left,
                    width: h.width,
                    height: h.height,
                    borderRadius: h.radius,
                  }}
                  onWheel={(e) => {
                    const scrollEl = findScrollableUnderPoint(e.clientX, e.clientY, e.currentTarget);
                    if (!scrollEl) return;
                    scrollEl.scrollTop += e.deltaY;
                    scrollEl.scrollLeft += e.deltaX;
                  }}
                  onTouchStart={(e) => {
                    const t = e.touches[0];
                    if (!t) return;
                    clickBlockTouchRef.current = {
                      x: t.clientX,
                      y: t.clientY,
                      scrollEl: findScrollableUnderPoint(t.clientX, t.clientY, e.currentTarget),
                    };
                  }}
                  onTouchMove={(e) => {
                    const state = clickBlockTouchRef.current;
                    const t = e.touches[0];
                    if (!state?.scrollEl || !t) return;
                    state.scrollEl.scrollTop += state.y - t.clientY;
                    state.scrollEl.scrollLeft += state.x - t.clientX;
                    state.x = t.clientX;
                    state.y = t.clientY;
                    e.preventDefault();
                  }}
                  onTouchEnd={() => {
                    clickBlockTouchRef.current = null;
                  }}
                  onTouchCancel={() => {
                    clickBlockTouchRef.current = null;
                  }}
                />
              ))
            : null}
        </>
      ) : hideDimForNavigation ? null : (
        <div className="cp-tutorial-dim cp-tutorial-dim-full" />
      )}

      <button
        ref={exitBtnRef}
        type="button"
        className="cp-tutorial-exit-btn"
        style={{ top: measureTopnavBottom() + EXIT_BTN_GAP_BELOW_NAV }}
        onClick={exitTutorial}
      >
        튜토리얼 종료
      </button>

      <div
        ref={bubbleElRef}
        className={`cp-tutorial-bubble cp-tutorial-bubble--${placement}${
          isIntro
            ? ' cp-tutorial-bubble--card cp-tutorial-bubble--intro'
            : isCelebration
              ? ' cp-tutorial-bubble--card cp-tutorial-bubble--celebration'
              : showSectionCard
                ? ' cp-tutorial-bubble--card cp-tutorial-bubble--section'
                : ''
        }${bubbleDragging ? ' is-dragging' : ''}${bubbleDragPos ? ' is-repositioned' : ''}${
          bubbleAnimationDone ? ' is-animation-done' : ''
        }`}
        style={bubbleStyle}
        onPointerDown={handleBubblePointerDown}
        onPointerMove={handleBubblePointerMove}
        onPointerUp={endBubbleDrag}
        onPointerCancel={endBubbleDrag}
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget) setBubbleAnimationDone(true);
        }}
      >
        {showBubbleTail ? (
          <span className={`cp-tutorial-bubble-tail cp-tutorial-bubble-tail--${placement}`} aria-hidden />
        ) : null}
        {isCardBubble ? (
          <>
            <div className="cp-tutorial-card-decor" aria-hidden>
              <span className="cp-tutorial-card-spark cp-tutorial-card-spark--a" />
              <span className="cp-tutorial-card-spark cp-tutorial-card-spark--b" />
              <span className="cp-tutorial-card-spark cp-tutorial-card-spark--c" />
            </div>
            <div className="cp-tutorial-card-head" title="드래그해서 말풍선 위치를 옮길 수 있어요">
              <div className="cp-tutorial-card-badge">
                <TutorialMascotAvatar />
                {isCelebration ? (
                  <span className="cp-tutorial-card-mark cp-tutorial-card-mark--check" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 18 18" focusable="false">
                      <path
                        d="M4.2 9.4 7.5 12.6 13.8 5.8"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                ) : isIntro ? (
                  <span className="cp-tutorial-card-mark cp-tutorial-card-mark--play" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 16 16" focusable="false">
                      <path d="M5 3.2v9.6L13 8 5 3.2z" fill="currentColor" />
                    </svg>
                  </span>
                ) : (
                  <span className="cp-tutorial-card-mark cp-tutorial-card-mark--flag" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                      <path
                        d="M3 1.5v11M3 2.2h7.2L8.8 4.8 10.2 7.4H3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </div>
              <p className="cp-tutorial-card-kicker">
                {isIntro
                  ? activeTutorial.title
                  : isCelebration
                    ? `${activeTutorial.title} 완료`
                    : `${activeTutorial.title} · 다음 구간`}
              </p>
              <h2 className="cp-tutorial-card-title">
                {showSectionCard ? renderTutorialSectionLabel(cardTitle) : cardTitle}
              </h2>
            </div>
            <p className="cp-tutorial-card-text">{renderTutorialMascotText(displayMascot)}</p>
            {showManualButton ? (
              <div className="cp-tutorial-bubble-actions cp-tutorial-bubble-actions--solo">
                <button type="button" className="cp-tutorial-btn-primary" onClick={handlePrimaryAdvance}>
                  {primaryLabel}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="cp-tutorial-bubble-head" title="드래그해서 말풍선 위치를 옮길 수 있어요">
              <TutorialMascotAvatar />
              <div className="cp-tutorial-bubble-head-text">
                <span className="cp-tutorial-title">{activeTutorial.title}</span>
                <span className="cp-tutorial-step-count">
                  {activeStepIndex + 1} / {totalSteps}
                  <span className="cp-tutorial-bubble-drag-hint"> · 드래그로 이동</span>
                </span>
              </div>
            </div>
            <p className="cp-tutorial-bubble-text">{renderTutorialMascotText(displayMascot)}</p>
            {searching && !followUpActive && !targetMissing && !leaveConfirmActive ? (
              <p className="cp-tutorial-bubble-hint">화면을 찾고 있어요…</p>
            ) : null}
            {showTargetMissingUi ? (
              <div className="cp-tutorial-bubble-recover">
                <p className="cp-tutorial-bubble-hint">{renderTutorialMascotText(targetMissingHintText)}</p>
                {/* manualAdvance 단계는 타깃을 못 찾아도 "다음"으로 건너뛸 수 있게 한다.
                    (보상 버튼·결과 모달이 없는 경우 등) */}
                {showManualButton ? (
                  <div className="cp-tutorial-bubble-actions cp-tutorial-bubble-actions--solo">
                    <button type="button" className="cp-tutorial-btn-primary" onClick={handlePrimaryAdvance}>
                      {primaryLabel}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {leaveConfirmActive ? (
              <p className="cp-tutorial-bubble-hint">
                저장하고 이동하거나 저장하지 않고 이동을 선택하면 계속 진행돼요.
              </p>
            ) : followUpActive && !followUpManualAdvance ? (
              <p className="cp-tutorial-bubble-hint">
                {followUp?.advanceSelector
                  ? '안내한 버튼을 누르면 다음으로 넘어가요.'
                  : '모달에서 진행하거나 취소하면 원래 안내로 돌아가요.'}
              </p>
            ) : !targetMissing && showManualButton ? (
              <>
                {!manualAdvanceButtonEnabled ? (
                  <p className="cp-tutorial-bubble-hint cp-tutorial-bubble-hint--gate">
                    안내한 조작(예: 셀 선택)을 하면 &quot;다음&quot;이 활성화돼요.
                  </p>
                ) : null}
                <div className="cp-tutorial-bubble-actions cp-tutorial-bubble-actions--solo">
                  <button
                    type="button"
                    className="cp-tutorial-btn-primary"
                    onClick={handlePrimaryAdvance}
                    disabled={!manualAdvanceButtonEnabled}
                    title={
                      manualAdvanceButtonEnabled
                        ? undefined
                        : '안내한 조작을 완료하면 버튼을 누를 수 있어요'
                    }
                  >
                    {primaryLabel}
                  </button>
                </div>
              </>
            ) : !targetMissing && !searching ? (
              <p className="cp-tutorial-bubble-hint cp-tutorial-bubble-hint--action">
                {step.advanceSelector
                  ? '안내한 버튼을 누르면 다음으로 넘어가요.'
                  : '반짝이는 표시 부분을 클릭하면 다음으로 넘어가요.'}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

/** 튜토리얼 단계는 페이지 상단을 기본으로 보여 준다. */
function resetTutorialPageScrollToTop() {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function collectVerticalScrollParents(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  let p: HTMLElement | null = el.parentElement;
  while (p) {
    if (p.classList.contains('cp-tutorial-root')) {
      p = p.parentElement;
      continue;
    }
    const style = window.getComputedStyle(p);
    const oy = style.overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      p.scrollHeight > p.clientHeight + 1
    ) {
      out.push(p);
    }
    p = p.parentElement;
  }
  return out;
}

function isTutorialTargetVisibleInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const minVisible = 64;
  const visibleH = Math.min(vh, rect.bottom) - Math.max(0, rect.top);
  const visibleW = Math.min(vw, rect.right) - Math.max(0, rect.left);
  return visibleH >= Math.min(minVisible, rect.height) && visibleW >= Math.min(minVisible, rect.width);
}

/**
 * 페이지·중첩 스크롤을 상단으로 맞춘 뒤, 설명 대상이 여전히 화면 밖에 있을 때만
 * 그 요소가 상단에 오도록 스크롤한다.
 */
function revealTutorialTargetPreferringPageTop(el: HTMLElement) {
  resetTutorialPageScrollToTop();
  for (const parent of collectVerticalScrollParents(el)) {
    parent.scrollTop = 0;
  }
  if (isTutorialTargetVisibleInViewport(el)) return;
  el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
}

/** 클릭 차단 오버레이 아래의 스크롤 가능 요소를 찾는다(목록 스크롤 전달용). */
function findScrollableUnderPoint(
  clientX: number,
  clientY: number,
  blockEl: HTMLElement
): HTMLElement | null {
  const prev = blockEl.style.pointerEvents;
  blockEl.style.pointerEvents = 'none';
  const under = document.elementFromPoint(clientX, clientY);
  blockEl.style.pointerEvents = prev;
  let el: Element | null = under;
  while (el instanceof HTMLElement) {
    if (el === blockEl || el.classList.contains('cp-tutorial-root')) {
      el = el.parentElement;
      continue;
    }
    const style = window.getComputedStyle(el);
    const canY =
      (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'overlay') &&
      el.scrollHeight > el.clientHeight + 1;
    const canX =
      (style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'overlay') &&
      el.scrollWidth > el.clientWidth + 1;
    if (canY || canX) return el;
    el = el.parentElement;
  }
  return null;
}

const CELEBRATION_CONFETTI_COLORS = [
  '#4c7cff',
  '#10504a',
  '#f0a04b',
  '#e85d75',
  '#7c5cfc',
  '#35c4a0',
  '#ff7a59',
  '#5ec8ff',
];
const CELEBRATION_CONFETTI_COUNT = 72;

/** 완료 카드용 색종이: 화면 전체에 흩날린 뒤 사라진다. */
function TutorialCelebrationFireworks({ animKey }: { animKey: string }) {
  return (
    <div className="cp-tutorial-confetti" key={animKey} aria-hidden>
      {Array.from({ length: CELEBRATION_CONFETTI_COUNT }, (_, i) => {
        const left = (i * 47 + (i % 9) * 13) % 100;
        const delay = (i % 18) * 55 + Math.floor(i / 12) * 40;
        const duration = 2100 + (i % 6) * 320;
        const drift = ((i * 17) % 160) - 80;
        const rot = 220 + (i % 8) * 120;
        const wobble = 18 + (i % 5) * 10;
        const top = -8 - (i % 10) * 2.2;
        const shape =
          i % 5 === 0 ? 'is-ribbon' : i % 3 === 0 ? 'is-square' : i % 4 === 0 ? 'is-round' : 'is-rect';
        return (
          <span
            key={i}
            className={`cp-tutorial-confetti-piece ${shape}`}
            style={
              {
                left: `${left}%`,
                top: `${top}%`,
                '--d': `${delay}`,
                '--dur': `${duration}`,
                '--drift': `${drift}px`,
                '--wobble': `${wobble}px`,
                '--rot': `${rot}deg`,
                background: CELEBRATION_CONFETTI_COLORS[i % CELEBRATION_CONFETTI_COLORS.length],
              } as React.CSSProperties
            }
          />
        );
      })}
    </div>
  );
}

const TUTORIAL_MASCOT_DOT_KINDS = new Set(['default', 'team', 'user']);

/** 마스코트 문구의 `**강조**`·`[[dot:default|team|user]]` 를 렌더한다. */
function renderTutorialMascotText(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\[\[dot:(?:default|team|user)\]\])/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={`em-${i}`} className="cp-tutorial-bubble-em">
          {part.slice(2, -2)}
        </strong>
      );
    }
    const dotMatch = part.match(/^\[\[dot:(default|team|user)\]\]$/);
    if (dotMatch && TUTORIAL_MASCOT_DOT_KINDS.has(dotMatch[1])) {
      return (
        <span
          key={`dot-${i}`}
          className={`cp-admin-learning-template-dot cp-admin-learning-template-dot--${dotMatch[1]} cp-tutorial-bubble-dot`}
          aria-hidden
        />
      );
    }
    return <React.Fragment key={`t-${i}`}>{part}</React.Fragment>;
  });
}

function holesEqual(a: TutorialSpotlightHole[], b: TutorialSpotlightHole[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (
      Math.abs(x.top - y.top) >= 0.5 ||
      Math.abs(x.left - y.left) >= 0.5 ||
      Math.abs(x.width - y.width) >= 0.5 ||
      Math.abs(x.height - y.height) >= 0.5 ||
      Math.abs(x.radius - y.radius) >= 0.5
    ) {
      return false;
    }
  }
  return true;
}

/** 말풍선 스타일로부터 대략적인 화면 좌표를 복원한다. */
function estimateBubbleRectFromStyle(
  style: React.CSSProperties,
  bubbleWidth: number,
  estHeight: number
): Rect | null {
  if (typeof window === 'undefined') return null;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const transform = typeof style.transform === 'string' ? style.transform : '';
  const centeredX =
    style.left === '50%' &&
    (transform.includes('translateX(-50%)') || transform.includes('translate(-50%'));
  const centeredXY = style.left === '50%' && transform.includes('translate(-50%, -50%)');

  let left: number | null = null;
  if (typeof style.left === 'number' && Number.isFinite(style.left)) {
    left = style.left;
    if (centeredXY) left = style.left - bubbleWidth / 2;
  } else if (centeredX || centeredXY) {
    left = (vw - bubbleWidth) / 2;
  } else if (typeof style.right === 'number' && Number.isFinite(style.right)) {
    left = vw - style.right - bubbleWidth;
  }

  let top: number | null = null;
  if (typeof style.top === 'number' && Number.isFinite(style.top)) {
    top = centeredXY ? style.top - estHeight / 2 : style.top;
  } else if (typeof style.bottom === 'number' && Number.isFinite(style.bottom)) {
    top = vh - style.bottom - estHeight;
  }

  if (left == null || top == null) return null;
  return { left, top, width: bubbleWidth, height: estHeight };
}

/**
 * 하이라이트와 말풍선이 가까울 때만 꼬리(삼각형)를 보여준다.
 * 배치 축 간격이 크거나, 꼬리 끝이 하이라이트에서 크게 어긋나면 숨긴다.
 */
function isBubbleTailNearHighlight(
  placement: Placement,
  holeRect: Rect | null,
  bubbleStyle: React.CSSProperties,
  bubbleWidth: number,
  estHeight: number
): boolean {
  if (!holeRect || placement === 'center' || placement === 'below-nav') return false;
  const bubbleRect = estimateBubbleRectFromStyle(bubbleStyle, bubbleWidth, estHeight);
  if (!bubbleRect) return false;

  const holeRight = holeRect.left + holeRect.width;
  const holeBottom = holeRect.top + holeRect.height;
  const bubbleRight = bubbleRect.left + bubbleRect.width;
  const bubbleBottom = bubbleRect.top + bubbleRect.height;

  let edgeGap = Infinity;
  if (placement === 'bottom') edgeGap = bubbleRect.top - holeBottom;
  else if (placement === 'top') edgeGap = holeRect.top - bubbleBottom;
  else if (placement === 'right') edgeGap = bubbleRect.left - holeRight;
  else if (placement === 'left') edgeGap = holeRect.left - bubbleRight;
  else return false;

  if (edgeGap < -8 || edgeGap > BUBBLE_TAIL_MAX_EDGE_GAP) return false;

  if (placement === 'bottom' || placement === 'top') {
    const tipX = bubbleRect.left + bubbleRect.width / 2;
    if (tipX >= holeRect.left && tipX <= holeRight) return true;
    const dx = tipX < holeRect.left ? holeRect.left - tipX : tipX - holeRight;
    return dx <= BUBBLE_TAIL_MAX_ALIGN_SLACK;
  }

  const tipY = bubbleRect.top + bubbleRect.height / 2;
  if (tipY >= holeRect.top && tipY <= holeBottom) return true;
  const dy = tipY < holeRect.top ? holeRect.top - tipY : tipY - holeBottom;
  return dy <= BUBBLE_TAIL_MAX_ALIGN_SLACK;
}

function resolvePlacement(
  preferred:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'auto'
    | 'center'
    | 'below-nav'
    | undefined,
  holeRect: Rect | null
): Placement {
  if (preferred === 'center') return 'center';
  if (preferred === 'below-nav') return 'below-nav';
  if (!holeRect) return 'center';
  if (preferred && preferred !== 'auto') return preferred;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const needH = BUBBLE_EST_HEIGHT + BUBBLE_GAP + BUBBLE_MARGIN;
  const needW = BUBBLE_WIDTH + BUBBLE_GAP + BUBBLE_MARGIN;
  const spaceBelow = vh - (holeRect.top + holeRect.height);
  const spaceAbove = holeRect.top;
  const spaceRight = vw - (holeRect.left + holeRect.width);
  const spaceLeft = holeRect.left;
  const candidates: Array<{ p: Exclude<Placement, 'center'>; space: number; need: number }> = [
    { p: 'bottom', space: spaceBelow, need: needH },
    { p: 'top', space: spaceAbove, need: needH },
    { p: 'right', space: spaceRight, need: needW },
    { p: 'left', space: spaceLeft, need: needW },
  ];
  const fitting = candidates.filter((c) => c.space >= c.need).sort((a, b) => b.space - a.space);
  if (fitting.length > 0) return fitting[0].p;
  return candidates.sort((a, b) => b.space - a.space)[0].p;
}

function computeBubbleStyle(
  placement: Placement,
  holeRect: Rect | null,
  bubbleWidth = BUBBLE_WIDTH,
  minTopBelowExit = getBubbleMinTopBelowExit()
): React.CSSProperties {
  const { width: vw, height: vh } = getLayoutViewportSize();
  if (placement === 'below-nav') {
    // 네비바 → 종료 버튼 → 말풍선 순으로, 좌우 중앙에 배치
    return {
      top: minTopBelowExit,
      left: '50%',
      transform: 'translateX(-50%)',
      width: bubbleWidth,
    };
  }
  if (!holeRect || placement === 'center') {
    const isHero = bubbleWidth >= BUBBLE_CELEBRATION_WIDTH;
    /**
     * 긴 시작/완료 카드는 translateY(-50%) 중앙 배치를 쓰지 않는다.
     * (1) 실제 높이가 예상보다 커 종료 버튼과 겹치고
     * (2) 등장 애니메이션이 translate(-50%,-50%)를 덮어쓰며
     * (3) exit-safe가 top만 바꾸면 → 위로 올랐다 다시 내려오는 점프가 난다.
     * 처음부터 종료 버튼 아래 상단 고정 + 좌우 중앙만 유지한다.
     */
    if (isHero) {
      const maxH = Math.max(200, vh - minTopBelowExit - BUBBLE_MARGIN);
      return {
        top: minTopBelowExit,
        left: '50%',
        transform: 'translateX(-50%)',
        width: bubbleWidth,
        maxHeight: maxH,
        overflowY: 'auto',
      };
    }
    // 짧은 중앙 카드: 화면 위쪽 중앙(확인 모달과 덜 겹치도록 0.3vh)
    const preferredCenterY = vh * 0.3;
    const halfEst =
      bubbleWidth >= BUBBLE_SECTION_WIDTH
        ? BUBBLE_SECTION_EST_HEIGHT / 2
        : BUBBLE_EST_HEIGHT / 2;
    const centerY = Math.max(preferredCenterY, minTopBelowExit + halfEst);
    return {
      top: centerY,
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: bubbleWidth,
    };
  }
  const centerX = holeRect.left + holeRect.width / 2;
  const centerY = holeRect.top + holeRect.height / 2;
  const clampLeft = (left: number) =>
    Math.min(Math.max(left, BUBBLE_MARGIN), Math.max(BUBBLE_MARGIN, vw - bubbleWidth - BUBBLE_MARGIN));
  const clampTopBelowExit = (top: number) => {
    const maxTop = Math.max(minTopBelowExit, vh - BUBBLE_EST_HEIGHT - BUBBLE_MARGIN);
    return Math.min(Math.max(top, minTopBelowExit), maxTop);
  };

  if (placement === 'bottom') {
    const left = clampLeft(centerX - bubbleWidth / 2);
    // 하이라이트 바로 아래. 종료 버튼·화면 밖으로만 클램프한다.
    const naturalTop = holeRect.top + holeRect.height + BUBBLE_GAP;
    return { top: clampTopBelowExit(naturalTop), left, width: bubbleWidth };
  }
  if (placement === 'top') {
    const left = clampLeft(centerX - bubbleWidth / 2);
    const naturalBottom = vh - holeRect.top + BUBBLE_GAP;
    // 말풍선이 위로 붙을 때도 종료 버튼 영역은 침범하지 않게 한다.
    const maxBottomByExit = Math.max(
      BUBBLE_MARGIN,
      vh - minTopBelowExit - BUBBLE_EST_HEIGHT
    );
    const maxBottom = Math.max(BUBBLE_MARGIN, vh - BUBBLE_EST_HEIGHT - BUBBLE_MARGIN);
    const bottom = Math.min(naturalBottom, maxBottom, maxBottomByExit);
    return { bottom: Math.max(bottom, BUBBLE_MARGIN), left, width: bubbleWidth };
  }

  const top = clampTopBelowExit(centerY - 100);
  if (placement === 'right') {
    const naturalLeft = holeRect.left + holeRect.width + BUBBLE_GAP;
    const maxLeft = Math.max(BUBBLE_MARGIN, vw - bubbleWidth - BUBBLE_MARGIN);
    const left = naturalLeft <= maxLeft ? naturalLeft : maxLeft;
    return { top, left, width: bubbleWidth };
  }
  const naturalRight = vw - holeRect.left + BUBBLE_GAP;
  const maxRight = Math.max(BUBBLE_MARGIN, vw - bubbleWidth - BUBBLE_MARGIN);
  const right = naturalRight <= maxRight ? naturalRight : maxRight;
  return { top, right, width: bubbleWidth };
}

export default TutorialOverlayHost;
